import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updatePassword,
  deleteUser,
  signOut as secondarySignOut,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { firebaseConfig, auth } from '@/firebase/config';
import {
  ref,
  rtdb,
  get,
  set,
  update,
  remove,
  serverTimestamp,
  userRef,
  usersRef,
  query,
  orderByChild,
  equalTo,
  snapshotToItem,
  snapshotToArray,
} from '@/firebase/database';
import type { AppUser } from '@/types';
import { createNotification } from './notificationService';

// Helper to get a secondary Firebase Auth instance so that creating/deleting a user does NOT log out the admin
const getSecondaryAuth = () => {
  const name = 'UserManagementApp';
  const app = getApps().find(a => a.name === name) || initializeApp(firebaseConfig, name);
  return getAuth(app);
};

const sanitizeEmail = (email: string) => email.toLowerCase().trim().replace(/[.#$[\]]/g, '_');

export const getUserByEmail = async (email: string): Promise<AppUser | null> => {
  const cleanEmail = email.toLowerCase().trim();
  try {
    const q = query(usersRef(), orderByChild('email'), equalTo(cleanEmail));
    const snap = await get(q);
    if (snap.exists()) {
      const items = snapshotToArray<AppUser>(snap);
      if (items.length > 0) {
        return { ...items[0], uid: (items[0] as any).id || items[0].uid };
      }
    }
  } catch (err) {
    console.warn('Query user by email failed, falling back to all scan:', err);
  }

  // Fallback scan
  try {
    const snap = await get(usersRef());
    if (snap.exists()) {
      const all = snapshotToArray<AppUser>(snap);
      const found = all.find(u => u.email && u.email.toLowerCase().trim() === cleanEmail);
      if (found) {
        return { ...found, uid: (found as any).id || found.uid };
      }
    }
  } catch (err) {
    console.error('getUserByEmail fallback scan error:', err);
  }
  return null;
};

export const getUserById = async (uid: string, emailFallback?: string): Promise<AppUser | null> => {
  const snap = await get(userRef(uid));
  if (snap.exists()) {
    const user = snapshotToItem<AppUser>(snap);
    return user ? { ...user, uid: snap.key as string } : null;
  }

  // If not found by UID but email is provided, check if user profile exists under another key
  if (emailFallback) {
    const userByEmail = await getUserByEmail(emailFallback);
    if (userByEmail) {
      // Migrate to the current Firebase Auth UID so future lookups are direct
      const { id, ...dataToSave } = userByEmail as any;
      await set(userRef(uid), {
        ...dataToSave,
        uid,
        updatedAt: serverTimestamp(),
      }).catch(() => {});

      if (userByEmail.uid && userByEmail.uid !== uid) {
        await remove(userRef(userByEmail.uid)).catch(() => {});
      }

      if (userByEmail.companyId) {
        await set(
          ref(rtdb, `companies/${userByEmail.companyId}/authEmailMap/${sanitizeEmail(emailFallback)}`),
          uid,
        ).catch(() => {});
      }
      return { ...userByEmail, uid };
    }
  }

  return null;
};

export const createUserProfile = async (
  uid: string,
  data: Omit<AppUser, 'uid' | 'createdAt' | 'updatedAt'>,
): Promise<void> => {
  await set(userRef(uid), {
    ...data,
    uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Store mapping for seamless user recreation after deletion
  if (data.email && data.companyId) {
    await set(
      ref(rtdb, `companies/${data.companyId}/authEmailMap/${sanitizeEmail(data.email)}`),
      uid,
    ).catch(() => {});
  }
};

export const updateUserProfile = async (uid: string, data: Partial<AppUser>): Promise<void> => {
  await update(userRef(uid), { ...data, updatedAt: serverTimestamp() });
};

export const deleteCompanyUser = async (uid: string, companyId?: string): Promise<void> => {
  // 1. Get user to know email before deletion
  const user = await getUserById(uid).catch(() => null);
  const email = user?.email?.toLowerCase().trim();

  // 2. Delete user account directly from Firebase Authentication via secondaryAuth
  if (email) {
    try {
      const secondaryAuth = getSecondaryAuth();
      const sanitized = sanitizeEmail(email);

      let knownPassword: string | null = null;
      if (companyId) {
        try {
          const passSnap = await get(
            ref(rtdb, `companies/${companyId}/authTempMap/${sanitized}`),
          );
          if (passSnap.exists()) knownPassword = passSnap.val();
        } catch {}
      }

      const candidatePasswords: string[] = [];
      if (knownPassword) {
        candidatePasswords.push(knownPassword);
      } else {
        // Fallback candidate passwords commonly used in development/testing
        candidatePasswords.push('zaki123', 'User@123456', '123456');
      }

      let authDeleted = false;
      for (const pwd of candidatePasswords) {
        if (authDeleted) break;
        try {
          const cred = await signInWithEmailAndPassword(secondaryAuth, email, pwd);
          if (cred.user) {
            await deleteUser(cred.user);
            console.log(`[Firebase Auth] Successfully deleted user account for ${email}`);
            authDeleted = true;
          }
          await secondarySignOut(secondaryAuth);
        } catch {
          // Try next candidate
        }
      }
    } catch (authErr) {
      console.warn('[Firebase Auth] Note on direct Auth account deletion:', authErr);
    }
  }

  // 3. Remove user profile from RTDB
  await remove(userRef(uid)).catch(() => {});

  // 4. Remove presence node and company mappings
  if (companyId) {
    await remove(ref(rtdb, `presence/${companyId}/${uid}`)).catch(() => {});
    if (email) {
      const sanitized = sanitizeEmail(email);
      await remove(ref(rtdb, `companies/${companyId}/authEmailMap/${sanitized}`)).catch(() => {});
      await remove(ref(rtdb, `companies/${companyId}/authTempMap/${sanitized}`)).catch(() => {});
    }
  }

  // 5. Remove any duplicate/orphan profiles with same email in RTDB
  if (email) {
    try {
      const snap = await get(usersRef());
      if (snap.exists()) {
        snap.forEach((child) => {
          if (child.val()?.email?.toLowerCase()?.trim() === email) {
            remove(child.ref).catch(() => {});
          }
        });
      }
    } catch {}
  }
};

export const getUsersByCompany = async (companyId: string): Promise<AppUser[]> => {
  try {
    const q = query(usersRef(), orderByChild('companyId'), equalTo(companyId));
    const snap = await get(q);
    if (snap.exists()) {
      return snapshotToArray<AppUser>(snap).map(u => ({ ...u, uid: (u as any).id || (u as any).uid }));
    }
  } catch (err) {
    console.warn('Query by index failed, trying fallback scan:', err);
  }

  // Resilient fallback: fetch all users and filter by companyId
  try {
    const snap = await get(usersRef());
    if (!snap.exists()) return [];
    const all = snapshotToArray<AppUser>(snap);
    return all
      .filter(u => u.companyId === companyId)
      .map(u => ({ ...u, uid: (u as any).id || (u as any).uid }));
  } catch (err) {
    console.error('getUsersByCompany error:', err);
    return [];
  }
};

export interface CreateCompanyUserInput {
  name: string;
  email: string;
  password?: string;
  roleId: string;
  mobile?: string;
  status?: 'active' | 'inactive';
}

export const createCompanyUser = async (
  companyId: string,
  input: CreateCompanyUserInput,
): Promise<{ uid: string; password: string }> => {
  const secondaryAuth = getSecondaryAuth();
  const cleanEmail = input.email.trim().toLowerCase();

  // Check if an active member with this email already exists in this company
  const currentMembers = await getUsersByCompany(companyId);
  const alreadyExistsInOrg = currentMembers.find(
    (m) => m.email.toLowerCase() === cleanEmail && m.status !== 'inactive',
  );
  if (alreadyExistsInOrg) {
    throw new Error('A user with this email address already exists in your organization.');
  }

  // Temporary password (at least 6 characters)
  const password =
    input.password && input.password.trim().length >= 6
      ? input.password.trim()
      : 'User@' + Math.random().toString(36).slice(-6) + '9!';

  let newUid = '';

  try {
    // 1. Attempt to create user in Firebase Auth via secondary auth instance
    const credential = await createUserWithEmailAndPassword(secondaryAuth, cleanEmail, password);
    newUid = credential.user.uid;
    await secondarySignOut(secondaryAuth);
  } catch (authErr: any) {
    // 2. If email is already in Firebase Auth (e.g. was previously deleted from RTDB), resolve UID
    if (authErr?.code === 'auth/email-already-in-use') {
      console.log(`[User Create] Email ${cleanEmail} exists in Auth. Linking/recreating profile...`);

      // Try signing in with the provided password
      try {
        const cred = await signInWithEmailAndPassword(secondaryAuth, cleanEmail, password);
        newUid = cred.user.uid;
        if (input.password && input.password.trim().length >= 6) {
          await updatePassword(cred.user, input.password.trim()).catch(() => {});
        }
        await secondarySignOut(secondaryAuth);
      } catch {
        // If password was different, check the authEmailMap or audit logs
        try {
          const mapSnap = await get(
            ref(rtdb, `companies/${companyId}/authEmailMap/${sanitizeEmail(cleanEmail)}`),
          );
          if (mapSnap.exists() && mapSnap.val()) {
            newUid = mapSnap.val();
          }
        } catch {}

        if (!newUid) {
          // Search audit logs for previous UID
          try {
            const auditSnap = await get(ref(rtdb, `companies/${companyId}/auditLogs`));
            if (auditSnap.exists()) {
              const logs = auditSnap.val();
              for (const key in logs) {
                if (logs[key]?.data?.email?.toLowerCase() === cleanEmail && logs[key]?.userId) {
                  newUid = logs[key].userId;
                  break;
                }
              }
            }
          } catch (e) {
            console.warn('Audit lookup fallback note:', e);
          }
        }
      }

      // If still no UID found, create stable user ID
      if (!newUid) {
        newUid = 'usr_' + Math.random().toString(36).substring(2, 10) + Date.now().toString(36);
      }
    } else {
      throw authErr;
    }
  }

  // 3. Create user record in Realtime Database
  await createUserProfile(newUid, {
    companyId,
    roleId: input.roleId,
    name: input.name.trim(),
    email: cleanEmail,
    mobile: input.mobile?.trim() || '',
    photoUrl: '',
    status: input.status || 'active',
  });

  // Save mapping and temporary credentials for clean auth deletion
  await set(
    ref(rtdb, `companies/${companyId}/authEmailMap/${sanitizeEmail(cleanEmail)}`),
    newUid,
  ).catch(() => {});
  await set(
    ref(rtdb, `companies/${companyId}/authTempMap/${sanitizeEmail(cleanEmail)}`),
    password,
  ).catch(() => {});

  createNotification({
    companyId,
    category: 'staff',
    type: 'staff_created',
    title: 'Staff Member Added',
    message: `${input.name.trim()} joined as ${input.roleId || 'Staff'}`,
    link: `/users`,
    userId: newUid,
    userName: input.name.trim(),
  }).catch(() => {});

  return { uid: newUid, password };
};

export const adminSendPasswordReset = async (email: string): Promise<void> => {
  await sendPasswordResetEmail(auth, email);
};

